import {
  type Enums,
  type TablesInsert,
  type TablesUpdate,
  type UserProfileDataType,
} from '@aikoa/common'
import { type User } from '@supabase/supabase-js'
import { omit } from 'lodash-es'
import { getCurrentUserProfile, parseUserProfile } from '~/data/user/readUserProfile'
import { supabase } from '~/lib/supabase'
import { trpcClient } from '~/lib/trpc'

export type CreateUserProfilePayload = {
  name: string
  title: string | undefined
  avatarUrl: string | undefined
  roles: Enums<'user_role'>[]
}

export type UpdateUserProfilePayload = Partial<Omit<CreateUserProfilePayload, 'roles'>> & {
  email?: string
  phoneNumber?: string
  user_id: string
}

export async function createUserProfile(payload: CreateUserProfilePayload) {
  const insertData = {
    name: payload.name,
    data: {
      title: payload.title,
      avatar_url: payload.avatarUrl,
    },
  } satisfies TablesInsert<'user_profile'>

  const { data, error } = await supabase
    .from('user_profile')
    .upsert(insertData, {
      onConflict: 'user_id',
      ignoreDuplicates: true,
    })
    .select('*, user_roles(role)')
    .single()

  if (error) {
    throw error
  }

  if (!data) {
    return null
  }

  const createRole = payload.roles?.[0]

  if (createRole) {
    await trpcClient.users.ensureRole.mutate({
      userId: data.user_id,
      role: createRole as Enums<'user_role'>,
    })
  }

  return parseUserProfile(data, null)
}

export async function updateUserProfile(payload: UpdateUserProfilePayload) {
  const existingProfile = await getCurrentUserProfile()

  const updateData = {
    name: payload.name || existingProfile?.name || existingProfile?.data?.name || '',
    user_id: payload.user_id,
    data: {
      ...omit(existingProfile?.data, 'name'),
      title: payload.title || existingProfile?.data?.title,
      avatar_url: payload.avatarUrl || existingProfile?.data?.avatar_url,
    } satisfies UserProfileDataType,
  } satisfies TablesUpdate<'user_profile'>

  const { data, error } = await supabase
    .from('user_profile')
    .upsert(updateData, {
      onConflict: 'user_id',
      ignoreDuplicates: false,
    })
    .eq('user_id', payload.user_id)
    .select('*, user_roles(role)')
    .single()

  if (error) {
    throw error
  }

  if (!data) {
    return null
  }

  return parseUserProfile(data, existingProfile?.organisation ?? null)
}

export async function verifyOrCreateUserProfile({
  user,
  createRole,
  profileData,
}: {
  user: User
  createRole: Enums<'user_role'>
  profileData?: UserProfileDataType
}) {
  const existingProfile = await getCurrentUserProfile()

  if (existingProfile) {
    const roles = existingProfile.roles

    if (!roles || roles.length === 0 || !roles.includes(createRole)) {
      await trpcClient.users.setExclusiveRole.mutate({
        userId: existingProfile.user_id,
        role: createRole,
      })

      return getCurrentUserProfile()
    }

    return existingProfile
  }

  return createUserProfile({
    name: profileData?.name ?? user.user_metadata.name ?? '',
    title: profileData?.title ?? user.user_metadata.title,
    avatarUrl: profileData?.avatar_url ?? user.user_metadata.avatar_url,
    roles: [createRole],
  })
}
